// Regras puras da leitura do mês: diferenças, percentuais restritos, sinais descritivos.
// Tudo aqui é descrição do dado observado. Nenhuma regra atribui causa
// (atraso, desperdício, produtividade, pagamento indevido, inadimplência).

import { SERIES_BY_KEY, SERIES_KEYS, formatBrl, formatMonthLabel, monthKeyFromDate } from "./monthlyProjectionModel";

const isValue = (cell) => cell?.status === "value" && Number.isFinite(cell.value);
const roundsToZero = (value) => Math.abs(Math.round(value * 100) / 100) === 0;

export const COMPARISONS = [
  {
    key: "planning",
    label: "Diferença do planejamento",
    formula: "Previsto − linha de base",
    minuend: "predicted",
    subtrahend: "baseline",
    question: "O valor planejado para este mês mudou em relação à base?",
    above: "acima da linha de base",
    below: "abaixo da linha de base",
    subject: "o previsto",
    percentageLabel: "Variação sobre a base",
    caveat: "Mudança entre base e previsto atuais; não indica a data ou o motivo de cada replanejamento.",
  },
  {
    key: "measuredVsPredicted",
    label: "Medido versus previsto",
    formula: "Medido − previsto",
    minuend: "measured",
    subtrahend: "predicted",
    question: "Quanto foi medido frente ao que estava previsto neste mês?",
    above: "acima do previsto",
    below: "abaixo do previsto",
    subject: "o medido",
    percentageLabel: "Medido sobre previsto",
    caveat: "A medição pode estar desatualizada e o mês em andamento; a curva mede valor ponderado, não dias.",
  },
  {
    key: "paidVsMeasured",
    label: "Pago versus medido",
    formula: "Pago − medido",
    minuend: "paid",
    subtrahend: "measured",
    question: "Quanto saiu de caixa frente ao valor da execução medida no mês?",
    above: "acima do medido",
    below: "abaixo do medido",
    subject: "o pago",
    percentageLabel: null,
    caveat: "Descasamento observável; prazo de pagamento, antecipação ou estoque são hipóteses de investigação.",
  },
];

export const computeDifference = (minuend, subtrahend) => {
  if (!isValue(minuend) || !isValue(subtrahend)) {
    return { status: "unavailable", reason: "missing_operand" };
  }
  return { status: "value", value: minuend.value - subtrahend.value };
};

const ratioPercentage = (base, numerator, transform) => {
  if (!isValue(base) || !isValue(numerator)) return { status: "not_applicable", reason: "missing_operand" };
  if (base.value < 0 || numerator.value < 0) return { status: "suppressed", reason: "negative_operand" };
  if (base.value === 0) return { status: "not_applicable", reason: "zero_base" };
  const value = transform(numerator.value, base.value);
  if (!Number.isFinite(value)) return { status: "not_applicable", reason: "non_finite" };
  return { status: "value", value };
};

// (previsto − base) / base × 100 — só com base positiva e previsto não negativo.
export const planningChangePercentage = (baseline, predicted) => (
  ratioPercentage(baseline, predicted, (numerator, base) => ((numerator - base) / base) * 100)
);

// medido / previsto × 100 — só com previsto positivo e medido não negativo.
// Não é avanço físico da atividade nem IDC.
export const predictedAttainmentPercentage = (predicted, measured) => (
  ratioPercentage(predicted, measured, (numerator, base) => (numerator / base) * 100)
);

export const describeDifference = ({ difference, comparison }) => {
  if (difference.status !== "value") {
    return {
      tone: "unavailable",
      text: "Sem os dois valores do mês, a diferença não é calculada.",
    };
  }
  if (roundsToZero(difference.value)) {
    return { tone: "neutral", text: "Sem diferença na precisão exibida." };
  }
  const direction = difference.value > 0 ? comparison.above : comparison.below;
  return {
    tone: "neutral",
    text: `Neste mês, ${comparison.subject} está ${formatBrl(Math.abs(difference.value))} ${direction}.`,
  };
};

const percentageFor = (comparison, cells) => {
  if (comparison.key === "planning") return planningChangePercentage(cells.baseline, cells.predicted);
  if (comparison.key === "measuredVsPredicted") return predictedAttainmentPercentage(cells.predicted, cells.measured);
  return { status: "omitted" };
};

export const computeMonthComparisons = (cells = {}) => COMPARISONS.map((comparison) => {
  const difference = computeDifference(cells[comparison.minuend], cells[comparison.subtrahend]);
  const description = describeDifference({ difference, comparison });
  return {
    key: comparison.key,
    label: comparison.label,
    formula: comparison.formula,
    question: comparison.question,
    caveat: comparison.caveat,
    percentageLabel: comparison.percentageLabel,
    difference,
    percentage: percentageFor(comparison, cells),
    tone: description.tone,
    text: description.text,
  };
});

export const monthTiming = (monthKey, now = new Date()) => {
  const current = monthKeyFromDate(now);
  if (!current || !monthKey) return "unknown";
  if (monthKey < current) return "past";
  if (monthKey > current) return "future";
  return "current";
};

const signal = (id, tone, text) => ({ id, tone, text });

export const buildMonthSignals = ({ monthKey, cells = {}, now = new Date() }) => {
  const timing = monthTiming(monthKey, now);
  const signals = [];
  const { baseline, predicted, measured, paid, toPay } = cells;

  if (timing === "current") {
    signals.push(signal("current_month", "info", "Mês em andamento; planejamento mensal comparado à medição disponível."));
  }
  if (timing === "future") {
    signals.push(signal(
      "future_month",
      "info",
      "Mês futuro: sem sinal de execução. A pagar são compromissos registrados para este vencimento, não todo o custo futuro do item.",
    ));
  }

  const negatives = SERIES_KEYS.filter((key) => isValue(cells[key]) && cells[key].value < 0);
  if (negatives.length > 0) {
    const labels = negatives.map((key) => SERIES_BY_KEY[key].label).join(", ");
    signals.push(signal(
      "negative_values",
      "info",
      `Valor negativo em ${labels}: ajuste ou estorno no mês. A diferença em reais é mantida e as razões percentuais são suprimidas.`,
    ));
  }

  const hasSchedulePoint = isValue(baseline) || isValue(predicted);
  if (isValue(paid) && !hasSchedulePoint) {
    signals.push(signal(
      "paid_without_schedule",
      "info",
      "Pagamento em mês sem ponto do cronograma. O cronograma aparece como “—”; ausência não é zero nem execução fora do plano.",
    ));
  }

  if (isValue(baseline) && isValue(predicted) && baseline.value === 0 && predicted.value === 0 && isValue(measured) && measured.value > 0) {
    signals.push(signal(
      "zero_plan_positive_measured",
      "info",
      "Base e previsto recebidos como zero com medido positivo: a diferença em reais é mostrada e o percentual não se aplica.",
    ));
  }

  if (timing === "past" && !isValue(measured) && isValue(predicted)) {
    signals.push(signal(
      "measured_missing",
      "info",
      "Sem medição informada para o mês; a comparação Medido versus previsto não é calculada. Ausência não é execução zero.",
    ));
  }

  if (timing === "past" && isValue(measured) && isValue(predicted)) {
    const difference = measured.value - predicted.value;
    if (!roundsToZero(difference) && difference < 0) {
      signals.push(signal(
        "measured_below_predicted",
        "neutral",
        `Medido ${formatBrl(Math.abs(difference))} abaixo do previsto em mês concluído. Confira a atualização da medição antes de concluir.`,
      ));
    }
    if (!roundsToZero(difference) && difference > 0) {
      signals.push(signal(
        "measured_above_predicted",
        "neutral",
        `Medido ${formatBrl(difference)} acima do plano mensal. Pode haver deslocamento de execução entre meses.`,
      ));
    }
  }

  if (isValue(paid) && isValue(measured)) {
    const difference = paid.value - measured.value;
    if (!roundsToZero(difference) && difference > 0) {
      signals.push(signal(
        "paid_above_measured",
        "neutral",
        `Pago ${formatBrl(difference)} acima do medido. Adiantamento, compra antecipada ou estoque são hipóteses para investigar nos títulos.`,
      ));
    }
    if (!roundsToZero(difference) && difference < 0) {
      signals.push(signal(
        "measured_above_paid",
        "neutral",
        `Execução medida supera a saída registrada em ${formatBrl(Math.abs(difference))}. Confira vencimentos, contratos e posição aberta.`,
      ));
    }
  }

  if (isValue(toPay) && toPay.value > 0) {
    if (timing === "past") {
      signals.push(signal(
        "open_balance_previous_month",
        "attention",
        `Saldo aberto com vencimento em mês anterior: ${formatBrl(toPay.value)}, pela posição da última carga. Não é confirmação em tempo real.`,
      ));
    }
    if (timing === "current") {
      signals.push(signal(
        "to_pay_current_mixed",
        "info",
        "A pagar deste mês pode incluir parcelas vencidas e a vencer; a série mensal não separa as duas. A distinção exige os títulos com datas.",
      ));
    }
  }

  return signals;
};

// Pico de A pagar entre os meses com valor informado no intervalo exibido.
export const findToPayPeak = (model) => {
  const informed = (model?.months ?? []).filter((month) => isValue(month.values.toPay));
  if (informed.length === 0) return { status: "none", informedMonths: 0 };
  const peak = Math.max(...informed.map((month) => month.values.toPay.value));
  return {
    status: "value",
    value: peak,
    monthKeys: informed.filter((month) => month.values.toPay.value === peak).map((month) => month.key),
    informedMonths: informed.length,
  };
};

export const describeToPayPeak = (peak) => {
  if (peak.status !== "value") return "Sem A pagar informado no intervalo exibido.";
  const months = peak.monthKeys.map((key) => formatMonthLabel(key));
  const where = peak.monthKeys.length > 1
    ? `${peak.monthKeys.length} meses empatados (${months.join(", ")})`
    : months[0];
  const informed = peak.informedMonths === 1 ? "o único mês informado" : `os ${peak.informedMonths} meses informados`;
  return `Maior A pagar entre ${informed} no intervalo: ${formatBrl(peak.value)} em ${where}. Não é o pico de toda a obra nem necessidade total de caixa.`;
};
